import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import {
  DotNestedKeys,
  FileInfo,
  MenuOptionValue,
  NestedPick,
  Paginated,
  PaginationQuery,
  PaginationQueryWithFields,
  Version,
  Versions,
} from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { AssignNewCarPlate, CarPlate, CarPlateNoCarDepency, CarPlateVersion, UpsertCarPlate } from "./types";

export const useCarPlateApi = () => {
  const api = useApi();

  const getAdminCarPlate = useCallback(
    (carPlateId: string, signal: AbortSignal) => {
      const url = `/admin/car_plates/${carPlateId}`;
      return useSWRMutation<CarPlate | null, CustomError, string>(url, (url) => api.get<CarPlate>({ url, signal }));
    },
    [api],
  );

  const useAdminCarPlates = useCallback(
    <F extends DotNestedKeys<CarPlateNoCarDepency>>(
      query: PaginationQueryWithFields<CarPlateNoCarDepency, F>,
      signal: AbortSignal,
      carId?: string,
    ) => {
      const url = buildUrl<NestedPick<CarPlateNoCarDepency, F>>(
        carId ? `/admin/cars/${carId}/plates` : `/admin/car_plates`,
        query,
      );
      return useSWR<Paginated<NestedPick<CarPlateNoCarDepency, F>> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<NestedPick<CarPlateNoCarDepency, F>>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminCarPlate = useCallback(
    async (body: UpsertCarPlate): Promise<{ id: string }> => {
      const url = `/admin/car_plates`;
      const id = await api.post<UpsertCarPlate, { id: string }>(url, body);
      api.mutateCache([
        url,
        `/admin/cars/${body.carId}/plates`,
        `/admin/car_plates/${id}/versions`,
        `/admin/cars/${body.carId}/overview`,
        `/my_cars/${body.carId}`,
      ]);
      return id;
    },
    [api],
  );

  const updateAdminCarPlate = useCallback(
    async (id: string, body: UpsertCarPlate) => {
      const url = `/admin/car_plates/${id}`;
      await api.put<UpsertCarPlate>(url, body);
      api.mutateCache([
        `/admin/car_plates`,
        `/admin/cars/${body.carId}/plates`,
        `/admin/car_plates/${id}/versions`,
        `/admin/cars/${body.carId}/overview`,
        `/my_cars/${body.carId}`,
      ]);
    },
    [api],
  );

  const deleteAdminCarPlate = useCallback(
    async (carPlateId: string, carId?: string): Promise<void> => {
      const url = `/admin/car_plates/${carPlateId}`;
      await api.del(url).then(() => {
        api.mutateCache([`/admin/car_plates`, `/admin/cars/${carId}/plates`, `/admin/due_dates`]);
      });
    },
    [api],
  );

  const assignNewPlate = useCallback(
    async (body: AssignNewCarPlate) => {
      const url = `/admin/cars/${body.carId}/assign_plate`;
      await api.post<AssignNewCarPlate, { id: string }>(url, body);
      api.mutateCache([
        `/admin/cars`,
        `/admin/car_plates`,
        `/admin/cars/${body.carId}/overview`,
        `/admin/cars/${body.carId}/plates`,
      ]);
    },
    [api],
  );

  const getAdminCarPlateHistory = useCallback(
    (query: PaginationQuery, carPlateID: string, signal: AbortSignal) => {
      const url = buildUrl(`/admin/car_plates/${carPlateID}/versions`, query);
      return useSWRImmutable<Paginated<Version> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Version>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminCarPlateVersion = useCallback(
    (carPlateId: string, versionNumber: number, signal: AbortSignal) => {
      const url = `/admin/car_plates/${carPlateId}/versions/${versionNumber}`;
      return useSWRImmutable<Versions<CarPlateVersion> | null, CustomError, string>(url, (url) =>
        api.get<Versions<CarPlateVersion>>({ url, signal }),
      );
    },
    [api],
  );

  const getFieldFilters = useCallback(
    (fieldName: string, signal: AbortSignal) => {
      const url = `/admin/car_plates/filters?field=${fieldName}`;
      return useSWRMutation<MenuOptionValue[] | null, CustomError, string>(url, (url) =>
        api.get<MenuOptionValue[]>({ url, signal }),
      );
    },
    [api],
  );

  const exportExcel = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/car_plates", query);
      const h = new Headers();
      h.append("Accept", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
      const response = api.get<FileInfo>({ url, signal, headers: h });
      return response;
    },
    [api],
  );

  return {
    updateAdminCarPlate,
    deleteAdminCarPlate,
    useAdminCarPlates,
    insertAdminCarPlate,
    getAdminCarPlate,
    assignNewPlate,
    getAdminCarPlateHistory,
    getAdminCarPlateVersion,
    getFieldFilters,
    exportExcel,
  };
};
